import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { cloneDeep } from 'lodash';
import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Period } from 'dashboard/lib/unixTime';
import { getEffectivePeriod } from 'dashboard/lib/utils/period';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import { MetricsGroupBy } from 'dashboard/models/metrics-verification';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { useListTransactionTypesQuery } from 'dashboard/redux/api/transaction-type';
import { getVerificationVolumeAnalyticsQueryTemplatesSelector } from 'dashboard/redux/selectors/analytics-query-templates';
import {
  getAdjustedFilters,
  getDynamicFilters,
  getIsSelectedVolumeQueryTemplateDifferentFromFilters,
  getVerificationTemplatesByType,
} from 'dashboard/redux/selectors/metrics-verifications';
import { getAllTemplates } from 'dashboard/redux/selectors/organization';
import {
  clearMetrics,
  FilterRulesForType,
  loadVolumeQueryTemplate,
  resetFiltersToInitialState,
  resetMetricsDateFilter,
  setGranularity,
  setMetricsFilters,
  setVerificationFilterRules,
  unsetSelectedVolumeQueryTemplate,
  VerificationMetricsFilterParams,
} from 'dashboard/redux/slices/metrics-verification';
import { RootState } from 'dashboard/redux/store';
import {
  loadMetricsVerificationFilteredVolumeSummary,
  loadMetricsVerificationVolumeIntervals,
} from 'dashboard/redux/thunk-actions/metrics-verification';
import DashboardVerificationAnalyticsVolumeView from 'dashboard/views/DashboardVerificationAnalyticsVolumeView';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useProductFeature from 'lib/hooks/useProductFeature';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import useTable from 'lib/hooks/useTable';
import { VerificationType } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const DashboardVerificationAnalyticsVolumeRoute = (
  props: PropsFromRedux & { openCreateQueryTemplateModal: () => void },
) => {
  const {
    currentEnvironment,
    filters,
    lastQueryFilters,
    filterRules,
    volumeLastQueryFilterRules,
    verificationTemplates,
    verificationGranularities,
    resetFiltersToInitialState,
    openCreateQueryTemplateModal,
    queryTemplates,
    selectedVolumeQueryTemplate,
    isQueryTemplateModified,
    unsetSelectedVolumeQueryTemplate,
  } = props;

  useEffectOnEnvironmentChange(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    props.clearMetrics();
  });

  const { handleDateUpdate } = useTable({
    filters,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setFilters: props.setMetricsFilters,
  });

  const { data: { data: transactionTypes = [] } = {}, error: listTransactionTypeError } =
    useListTransactionTypesQuery({});
  useRTKErrorHandler({
    error: listTransactionTypeError,
    onError: () => ({ message: 'Failed to load transaction types.', type: MessageType.Failure }),
  });

  const {
    loading,
    error,
    callback: loadVolumeMetrics,
  } = useApiCallback(async () => {
    // When one runs a new load for the verification metrics, we'll need to ensure that their new date range
    // does not require that we reset the granularities for one or many verification types
    const granularityCopy = cloneDeep(verificationGranularities);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    Object.keys(verificationGranularities).forEach((verificationType: VerificationType) => {
      const recommendedPeriod = getEffectivePeriod(verificationGranularities[verificationType], [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);
      granularityCopy[verificationType] = recommendedPeriod;
    });

    await Promise.all([
      props.setGranularity({
        verificationGranularity: granularityCopy,
        verificationSurface: 'volume',
      }),
      props.loadMetricsVerificationFilteredVolumeSummary({
        currentEnvironment,
        filters,
        filterRules,
        verificationTemplates,
      }),
      props.loadMetricsVerificationVolumeIntervals({
        currentEnvironment,
        filters,
        filterRules,
        verificationTemplates,
        verificationGranularities: granularityCopy,
      }),
    ]);
  }, [currentEnvironment, filters, filterRules, verificationTemplates, verificationGranularities]);

  const {
    loading: loadingGranular,
    error: errorGranular,
    callback: handleUpdateGranularity,
  } = useApiCallback(
    async (type: VerificationType, period: Period) => {
      // Periods can be updated in tandem with a new set of filters, so we'll need to ensure that the new
      // periodicity is not in conflict with the new date range
      const granularityCopy = cloneDeep(props.verificationGranularities);
      granularityCopy[type] = getEffectivePeriod(period, [
        filters.createdAtStart,
        filters.createdAtEnd,
      ]);

      // Also, ensure that we validate all existing granularities since they are also affected
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      Object.keys(granularityCopy).forEach((verificationType: VerificationType) => {
        const recommendedPeriod = getEffectivePeriod(granularityCopy[verificationType], [
          filters.createdAtStart,
          filters.createdAtEnd,
        ]);
        granularityCopy[verificationType] = recommendedPeriod;
      });

      await Promise.all([
        props.setGranularity({
          verificationGranularity: granularityCopy,
          verificationSurface: 'volume',
        }),
        props.loadMetricsVerificationVolumeIntervals({
          currentEnvironment,
          filters,
          filterRules,
          verificationTemplates,
          verificationGranularities: granularityCopy,
        }),
      ]);
    },
    [currentEnvironment, filterRules, verificationGranularities, verificationTemplates, filters],
  );

  useApiErrorHandler({
    error: errorGranular,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useApiErrorHandler({
    error,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification metrics.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const handleTemplateFilterUpdate = useCallback((templateIds: string[]) => {
    props.setMetricsFilters({
      templateIds: templateIds,
    });
  }, []);

  const handleVerificationTypesUpdate = useCallback((verificationTypes: string[]) => {
    props.setMetricsFilters({
      verificationTypes,
    });
  }, []);

  const handleTxnTypesFilterUpdate = useCallback((txnTypeIds: string[]) => {
    props.setMetricsFilters({
      txnTypeIds,
    });
  }, []);

  const handleGroupByFilterToggle = useCallback((checked: boolean) => {
    props.setMetricsFilters({ groupBy: checked ? MetricsGroupBy.Inquiry : undefined });
  }, []);

  const handleAddAndRule = (type: FilteredVerificationType, orRuleIndex: number) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    const newRules = cloneDeep(props.filterRules);
    newRules[type].or[orRuleIndex] = newRules[type].or[orRuleIndex].concat({} as FilterRule);
    props.setVerificationFilterRules({
      filterRules: newRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const handleAddOrRule = (type: VerificationType) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    const newRules = cloneDeep(props.filterRules);
    newRules[type].or = newRules[type].or.concat([[{} as FilterRule]]);
    props.setVerificationFilterRules({
      filterRules: newRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const handleBulkRulesUpdate = (filterRules: Record<VerificationType, FilterRulesForType>) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    props.setVerificationFilterRules({
      filterRules: filterRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const handleDeleteAndRule = (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
  ) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    const newRules = cloneDeep(props.filterRules);
    newRules[type].or[orRuleIndex].splice(andRuleIndex, 1);

    // When we delete all rules in an orRule group, remove it
    if (newRules[type].or[orRuleIndex].length === 0 && orRuleIndex > 0) {
      newRules[type].or.splice(orRuleIndex, 1);
    }

    props.setVerificationFilterRules({
      filterRules: newRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const handleUpdateAndRule = (
    type: FilteredVerificationType,
    orRuleIndex: number,
    andRuleIndex: number,
    dimension?: FilterDimensions,
    operator?: string,
    values?: string[],
  ) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    const filterRulesCopy = cloneDeep(props.filterRules);
    const newRules = {
      ...filterRulesCopy,
      // due to the deeply nested structure of filter rules
      // clone deep for the specific type to avoid losing/overwriting changes
      [type]: cloneDeep(props.filterRules[type]),
    };

    if (dimension != null) {
      newRules[type].or[orRuleIndex][andRuleIndex].dimension = dimension;
    }

    if (operator != null) {
      newRules[type].or[orRuleIndex][andRuleIndex].operator = operator;
    }

    if (values != null) {
      newRules[type].or[orRuleIndex][andRuleIndex].values = values;
    }

    props.setVerificationFilterRules({
      filterRules: newRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const {
    loading: loadingGroup,
    error: errorGrouping,
    callback: handleGroupByDimensionUpdate,
  } = useApiCallback(
    async (type: FilteredVerificationType, dimension: FilterDimensions) => {
      const dimensionsCopy = cloneDeep(props.filters.groupByDimension);
      const filtersCopy: VerificationMetricsFilterParams = cloneDeep(props.filters);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      dimensionsCopy[type] = dimension as FilterDimensions;
      props.setMetricsFilters({
        ...filtersCopy,
        groupByDimension: dimensionsCopy,
      });
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filtersCopy.groupByDimension[type] = dimension as FilterDimensions;
      await Promise.all([
        props.loadMetricsVerificationVolumeIntervals({
          currentEnvironment,
          filters: filtersCopy,
          filterRules,
          verificationTemplates,
          verificationGranularities,
        }),
      ]);
    },
    [currentEnvironment, filterRules, verificationGranularities, filters, verificationTemplates],
  );

  useApiErrorHandler({
    error: errorGrouping,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verification metrics.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const handleVerificationTemplateFilterUpdate = (
    type: VerificationType,
    verificationTemplateIds: string[],
  ) => {
    const lastRules = cloneDeep(props.volumeLastQueryFilterRules);
    const filterRulesClone = cloneDeep(props.filterRules);
    const newRules = {
      ...filterRulesClone,
      [type]: {
        ...filterRulesClone[type],
        verificationTemplateIds: [...verificationTemplateIds],
        // In the event that there is a the null (None) selection made, we will include legacy verifications
        includeLegacyVerifications:
          verificationTemplateIds.findIndex((templateId) => templateId == null) >= 0,
      },
    };

    props.setVerificationFilterRules({
      filterRules: newRules,
      volumeLastQueryFilterRules: lastRules,
    });
  };

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      // Ensure we have a date range before we extract dates
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({
              startDate,
              endDate,
            })
          : /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            props.resetMetricsDateFilter();
      }
    },
    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  const resetToInitialStateAndLoad = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    async () => resetFiltersToInitialState(),
    [resetFiltersToInitialState],
  );

  const handleLoadQueryTemplate = useCallback((template: AnalyticsQueryTemplate) => {
    props.loadVolumeQueryTemplate({
      queryTemplate: template,
    });
  }, []);

  const productFeatureIsDisabled = !useProductFeature(ProductFeatureName.VerificationsAnalytics);

  return (
    <DashboardVerificationAnalyticsVolumeView
      error={error != null || errorGrouping != null}
      filterRules={props.filterRules}
      filteredVolumeSummary={props.filteredVolumeSummary}
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleAddAndRule={handleAddAndRule}
      handleAddOrRule={handleAddOrRule}
      handleBulkRulesUpdate={handleBulkRulesUpdate}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
      handleDeleteAndRule={handleDeleteAndRule}
      handleGroupByDimensionUpdate={handleGroupByDimensionUpdate}
      handleGroupByFilterToggle={handleGroupByFilterToggle}
      handleSetFilters={handleVerificationTypesUpdate}
      handleTemplateFilterUpdate={handleTemplateFilterUpdate}
      handleTxnTypesFilterUpdate={handleTxnTypesFilterUpdate}
      handleUpdateAndRule={handleUpdateAndRule}
      handleUpdateGranularity={handleUpdateGranularity}
      handleVerificationTemplateFilterUpdate={handleVerificationTemplateFilterUpdate}
      lastQueryFilters={lastQueryFilters}
      loadVolumeMetrics={loadVolumeMetrics}
      loading={loading || loadingGroup || loadingGranular}
      productFeatureIsDisabled={productFeatureIsDisabled}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      queriedAt={props.queriedAt}
      templates={props.templates}
      verificationTemplates={props.verificationTemplates}
      txnTypes={transactionTypes}
      dynamicFilterOptions={props.dynamicFilterOptions}
      verificationVolumeOverTime={props.verificationVolumeOverTime}
      volumeLastQueryFilterRules={volumeLastQueryFilterRules}
      verificationGranularities={props.verificationGranularities}
      volumeLastGranularities={props.lastVerificationGranularities.volume}
      resetFiltersToInitialState={resetToInitialStateAndLoad}
      openCreateQueryTemplateModal={openCreateQueryTemplateModal}
      queryTemplates={queryTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedVolumeQueryTemplate={selectedVolumeQueryTemplate}
      handleLoadQueryTemplate={handleLoadQueryTemplate}
      isQueryTemplateModified={isQueryTemplateModified}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      unsetSelectedVolumeQueryTemplate={unsetSelectedVolumeQueryTemplate}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  filterRules: state.metricsVerification.filterRules,
  filteredVolumeSummary: state.metricsVerification.filteredVolumeSummary,
  filters: getAdjustedFilters(state),
  lastQueryFilters: state.metricsVerification.lastQueryFilters,
  queriedAt: state.metricsVerification.queriedAt.filteredVolume,
  templates: getAllTemplates(state),
  verificationTemplates: getVerificationTemplatesByType(state),
  dynamicFilterOptions: getDynamicFilters(state),
  verificationVolumeOverTime: state.metricsVerification.verificationVolumeOverTime,
  volumeLastQueryFilterRules: state.metricsVerification.volumeLastQueryFilterRules,
  verificationGranularities: state.metricsVerification.verificationGranularities,
  lastVerificationGranularities: state.metricsVerification.lastVerificationGranularities,
  queryTemplates: getVerificationVolumeAnalyticsQueryTemplatesSelector(state),
  selectedVolumeQueryTemplate: state.metricsVerification.selectedVolumeQueryTemplate.template,
  isQueryTemplateModified: getIsSelectedVolumeQueryTemplateDifferentFromFilters(state),
});

const mapDispatchToProps = {
  clearMetrics,
  loadMetricsVerificationFilteredVolumeSummary,
  loadMetricsVerificationVolumeIntervals,
  resetMetricsDateFilter,
  setMetricsFilters,
  setVerificationFilterRules,
  setGranularity,
  showMessage,
  resetFiltersToInitialState,
  loadVolumeQueryTemplate,
  unsetSelectedVolumeQueryTemplate,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardVerificationAnalyticsVolumeRoute);
